"use client";

// CTA de postulación del detalle del job — encapsula 2 estados client-side:
//
//   1. "Nunca postulé a este job" (default): botón "Postularme" grande e
//      IntersectionObserver-driven — cuando el botón sale del viewport al
//      hacer scroll, aparece un floating action button para que el CTA
//      quede siempre a un tap de distancia. Antes había sólo un sticky
//      bottom en mobile; ahora funciona igual en desktop.
//
//   2. "Ya me postulé a este job": lee el registro de postulaciones que
//      guardamos al hacer submit exitoso (localStorage.talenv_applied_v1)
//      y reemplaza el CTA por un banner "Ya te postulaste el <fecha>" con
//      link a /portal/<slug>/my-application. Evita que el candidato mande
//      la misma postulación 2 veces sin darse cuenta.
//
// Nota: NO reemplaza los guards del backend (dedup por email+job en la
// tabla applications). Solo mejora la UX del cliente — si el user cambia
// de browser o borra localStorage, el server sigue haciendo dedup.

import { useEffect, useState } from "react";
import Link from "next/link";
import { CheckCircle } from "lucide-react";

const LS_APPLIED = "talenv_applied_v1";

type AppliedRecord = {
  date: string; // ISO
  jobTitle: string;
  companySlug: string;
};

function readApplied(jobId: string): AppliedRecord | null {
  try {
    const raw = localStorage.getItem(LS_APPLIED);
    if (!raw) return null;
    const dict = JSON.parse(raw) as Record<string, AppliedRecord>;
    return dict?.[jobId] ?? null;
  } catch {
    return null;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-AR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function ApplyCTA({
  slug,
  jobId,
  accent,
}: {
  slug: string;
  jobId: string;
  accent: string;
}) {
  // hydrated=false hasta que corra useEffect en cliente — evita mismatch
  // de hidratación (el server no puede leer localStorage). Renderizamos
  // el botón normal en SSR y ajustamos al montar si detectamos "applied".
  const [hydrated, setHydrated] = useState(false);
  const [applied, setApplied] = useState<AppliedRecord | null>(null);
  const [ctaVisible, setCtaVisible] = useState(true);

  useEffect(() => {
    setHydrated(true);
    setApplied(readApplied(jobId));
  }, [jobId]);

  // IntersectionObserver del CTA principal — cuando sale del viewport,
  // mostramos el floating action button. Se recrea cada vez que hydrated
  // cambia para que el nodo target exista.
  useEffect(() => {
    if (!hydrated || applied) return;
    const el = document.getElementById("primary-apply-cta");
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setCtaVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hydrated, applied]);

  // Estado "ya postulé" — reemplaza el CTA por un banner informativo.
  if (hydrated && applied) {
    return (
      <div className="rounded-2xl border border-[var(--pborder)] bg-[var(--pcard)] p-5 sm:p-6 space-y-3">
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: `${accent}20` }}
          >
            <CheckCircle size={20} style={{ color: accent }} strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <p className="font-jakarta font-semibold text-[var(--pfg)] text-base">
              Ya te postulaste a esta posición
            </p>
            <p className="text-sm text-[var(--pmuted)] mt-0.5">
              Enviaste tu postulación el <strong>{formatDate(applied.date)}</strong>. Te contactamos por email cuando haya novedades.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-1">
          <Link
            href={`/portal/${slug}/my-application`}
            className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: accent }}
          >
            Ver estado de mi postulación
          </Link>
          <Link
            href={`/portal/${slug}`}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold border border-[var(--pborder-strong)] text-[var(--pfg)] hover:bg-[var(--phover)] transition-colors"
          >
            Ver otras posiciones
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* CTA en flujo — grande, prominente. IntersectionObserver lo trackea. */}
      <div id="primary-apply-cta">
        <Link
          href={`/portal/${slug}/jobs/${jobId}/apply`}
          className="flex items-center justify-center w-full py-4 rounded-2xl text-white font-semibold text-base shadow-lg transition-opacity hover:opacity-90"
          style={{ backgroundColor: accent }}
        >
          Postularme a esta posición
        </Link>
      </div>

      {/* Floating action button — aparece cuando el CTA en-flow sale del
          viewport. Fixed bottom, más chico que el original, con la misma
          acción. Funciona igual en mobile y desktop.
          Renderizamos con opacity/translate en vez de mount/unmount para
          animar la aparición y no pisar el foco. */}
      <div
        aria-hidden={ctaVisible || !hydrated}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 transition-all duration-200 ${
          !hydrated || ctaVisible
            ? "opacity-0 translate-y-3 pointer-events-none"
            : "opacity-100 translate-y-0"
        }`}
      >
        <Link
          href={`/portal/${slug}/jobs/${jobId}/apply`}
          className="inline-flex items-center gap-2 px-5 h-12 rounded-full text-white text-sm font-semibold shadow-xl hover:opacity-90 transition-opacity"
          style={{ backgroundColor: accent }}
          tabIndex={ctaVisible ? -1 : 0}
        >
          Postularme
        </Link>
      </div>
    </>
  );
}
